import { getChromeStoreExtensionId } from "./appConfig";
import { getCookie } from "./utils";
import { isStandalone, navigateStandalone } from "./hostMode";

let extensionIdFromLocalStorage = localStorage.getItem("extensionId");
let extensionIdFromURL = new URLSearchParams(window.location.search).get(
  "extensionId"
);
let extensionIdFromCookie = getCookie("extensionId");

const FALLBACK_TOAST_CONTAINER_ID = "fallback-message-toast-container";
const FALLBACK_TOAST_HIDE_DELAY = 6000;

// Shown when the extension isn't there to receive "show-error-message" /
// "show-success-message" (e.g. the admin site) — otherwise the feedback
// vanishes silently.
const showFallbackToast = (text, variant) => {
  if (!text) {
    return;
  }

  let container = document.getElementById(FALLBACK_TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement("div");
    container.id = FALLBACK_TOAST_CONTAINER_ID;
    container.style.cssText =
      "position:fixed;top:16px;right:16px;z-index:100000;display:flex;flex-direction:column;gap:8px;max-width:360px;";
    document.body.appendChild(container);
  }

  const background = variant === "error" ? "#de6363" : "#4a4a4a";
  const toast = document.createElement("div");
  toast.setAttribute("role", variant === "error" ? "alert" : "status");
  toast.style.cssText = `background:${background};color:#fff;padding:12px 16px;border-radius:6px;font-size:14px;font-family:inherit;box-shadow:0 2px 8px rgba(0,0,0,0.25);cursor:pointer;`;
  toast.textContent = text;
  toast.onclick = () => toast.remove();
  container.appendChild(toast);

  setTimeout(() => toast.remove(), FALLBACK_TOAST_HIDE_DELAY);
};

const showFallbackIfUndelivered = (message) => {
  if (message?.message === "show-error-message") {
    showFallbackToast(message?.data?.message, "error");
  } else if (message?.message === "show-success-message") {
    showFallbackToast(message?.data?.message, "neutral");
  }
};

// chrome.runtime.lastError "message port closed" means the message WAS delivered
// but the listener sent no response — that is the only lastError that counts as
// delivered. Everything else ("receiving end does not exist", "could not
// establish connection", "Invalid extension id", …) means nothing received it;
// an allowlist here once let a garbage persisted extensionId count as delivered
// and hang the app on Loading forever.
const isUndeliveredError = (lastError) =>
  Boolean(lastError) && !/message port closed/i.test(lastError?.message || "");

// What a standalone tab still sends to an installed extension: keeping the
// extension's session in step with this one, and detecting it. Every other message
// makes the extension act on the tab it came from (open or close its panel, watch
// the page for contacts), and on the standalone site the extension stays out of the
// way unless the user opens it from its own popup.
const STANDALONE_EXTENSION_MESSAGES = new Set([
  "save-value-to-storage",
  "logged-in",
  "refreshed-token",
  "sign-out",
  "get-storage-data",
  "ping",
  "is-extension-installed",
]);

// The in-app equivalent of what the extension does with a navigation message.
const getStandaloneRoute = ({ message, data } = {}) => {
  switch (message) {
    case "open-campaigns":
    case "close-iframe":
    case "finish-create-campaign":
    case "cancel-campaign-creation":
      return "/campaigns";
    case "open-campaign-preview":
    case "open-campaign-details-page":
      return data?.campaignId
        ? `/campaigns?campaignId=${encodeURIComponent(data.campaignId)}`
        : "/campaigns";
    case "need-verify":
      return "/verification";
    case "sign-out":
      return "/login";
    default:
      return null;
  }
};

// Chrome extension ids are exactly 32 chars of a-p. Persisting anything else
// (a mangled ?extensionId= param) poisons every later send — and clearCache
// deliberately preserves extensionId, so the poison would never age out.
const isValidExtensionId = (id) => /^[a-p]{32}$/.test(id || "");

// `onUndelivered` (optional) is invoked when the message could not reach any
// extension at all — no chrome.runtime, no extension id, or "receiving end does
// not exist" on every id tried. Callers use it to fall back to an in-web action
// (e.g. openCampaignPreview navigating to the campaign instead). Note it cannot
// detect an extension that received the message but ignored the type.
//
// `onDelivered` (optional) is its mirror: some id in the chain accepted the
// message. Only pingExtension needs it, and it needs it precisely BECAUSE of the
// fallback chain — a stale persisted id must not be read as "no extension" when
// the store id would have worked.
const sendMessageToExtension = (message, onUndelivered, onDelivered) => {
  const handleUndelivered = () => {
    showFallbackIfUndelivered(message);
    onUndelivered?.();
  };

  if (isStandalone()) {
    const route = getStandaloneRoute(message);

    if (!STANDALONE_EXTENSION_MESSAGES.has(message?.message)) {
      // A caller's own fallback already knows where to go; otherwise route here.
      handleUndelivered();
      if (!onUndelivered && route) {
        navigateStandalone(route);
      }
      return;
    }

    if (route) {
      navigateStandalone(route);
    }

    if (message.message === "logged-in") {
      // The extension would otherwise drop its campaigns panel over this tab.
      message = {
        ...message,
        data: { ...message.data, isRedirectDisabled: true },
      };
    }
  }

  let extensionId =
    extensionIdFromLocalStorage ||
    extensionIdFromURL ||
    extensionIdFromCookie ||
    getChromeStoreExtensionId();

  // eslint-disable-next-line no-undef
  let chromeRuntime = typeof chrome !== "undefined" ? chrome.runtime : null;

  if (!chromeRuntime?.sendMessage || !extensionId) {
    handleUndelivered();
    return;
  }

  // Each attempt is wrapped in its own try/catch: sendMessage can THROW
  // synchronously on a malformed id, and the retry runs inside the previous
  // attempt's async callback — outside any caller-level try.
  const sendTo = (id, onSendUndelivered) => {
    try {
      chromeRuntime.sendMessage(id, message, () => {
        if (isUndeliveredError(chromeRuntime.lastError)) {
          onSendUndelivered();
          return;
        }
        onDelivered?.();
      });
    } catch (error) {
      console.log(`Error: ${error.message}; stack: ${error.stack}`);
      onSendUndelivered();
    }
  };

  const storeExtensionId = getChromeStoreExtensionId();
  const tried = new Set();

  // Fallback chain: persisted/URL/cookie id first, then the URL id if different,
  // then the chrome-store id as the last resort — a stale persisted id must not
  // permanently cut off a normally-installed extension.
  const tryNext = (candidates) => {
    const next = candidates.find((id) => id && !tried.has(id));
    if (!next) {
      handleUndelivered();
      return;
    }
    tried.add(next);
    sendTo(next, () => tryNext(candidates));
  };

  if (extensionIdFromURL && isValidExtensionId(extensionIdFromURL)) {
    localStorage.setItem("extensionId", extensionIdFromURL);
  }

  tryNext([extensionId, extensionIdFromURL, storeExtensionId]);
};

/**
 * Whether the extension is actually installed in this browser.
 *
 * sendMessageToExtension is fire-and-forget by design — nothing else in the app needs
 * to know whether a message landed. The Blackbaud install handoff does: it is the one
 * place where "did the extension get added?" decides what the page tells the user, and
 * claiming they are all set when the extension is missing would send them back to
 * Blackbaud to look for a button that is not there.
 *
 * Deliberately built on sendMessageToExtension rather than calling chrome directly, so
 * it inherits the id fallback chain. Asking a single id would report "not installed"
 * for anyone carrying a stale persisted extensionId, which is exactly the population
 * most likely to be reinstalling.
 *
 * The message itself matches no case in the extension's external-message switch, so it
 * is inert on arrival — the only thing being measured is whether it arrives at all.
 *
 * @returns {Promise<boolean>}
 */
const PING_TIMEOUT_MS = 3000;

const pingExtension = () =>
  new Promise((resolve) => {
    let settled = false;
    const settle = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeoutId);
      resolve(value);
    };

    // If neither callback ever fires, the caller must not be stranded mid-flow. False
    // is the safe wrong answer: it offers the extension again, which is harmless if it
    // is already there, whereas a wrong true sends someone to Blackbaud to hunt for a
    // button that may not exist.
    const timeoutId = setTimeout(() => settle(false), PING_TIMEOUT_MS);

    sendMessageToExtension(
      { message: "ping" },
      () => settle(false),
      () => settle(true)
    );
  });

export { sendMessageToExtension, pingExtension };
